import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useAssignAllApartmentsToGroupMutation } from '../../apiRtk/be4feApi';
import actions from '../../modules/accessSettings';
import { TYPE_DESCRIPTOR } from '../../modules/notifications';
import { addSnackbarError, addSnackbarSuccess } from '../../modules/notifications/actions';
import Alert from '../Alert';
import ApartmentAutocomplete from '../ApartmentAutocomplete';
import PrimaryModal from '../PrimaryModal';
import { useGetApartmentFormData } from './hooks/getApartmentFormData';
import { useGetGlobalHooks } from './hooks/getGlobalHooks';
import messages from './messages';

interface ApartmentFormData {
  apartment: { id: string }[];
  floor: string;
  hasForm: boolean;
  name: string;
  number: string;
}

interface AccessGroupAddApartmentModalProps {
  companyId: number;
  groupId: string;
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  siteId: number;
}

function AccessGroupAddApartmentModal({
  companyId,
  groupId,
  onClose,
  onSuccess,
  open,
  siteId,
}: AccessGroupAddApartmentModalProps) {
  const hasFormItemKey = 'hasForm';
  const newItemId = 'newItemId';
  const addAllApartmentsId = 'addAllApartmentsId';
  const { isLoading, isOptionsLoading, optionsData } = useGetApartmentFormData();
  const { dispatch, formatMessage, formMethods: rawFormMethods } = useGetGlobalHooks(hasFormItemKey);
  const formMethods = rawFormMethods as unknown as UseFormReturn<ApartmentFormData>;
  const { isDirty, isValid } = formMethods.formState;
  const apartments = formMethods.getValues('apartment') ?? [];
  const isAddAllApartmentsSelected = apartments.length > 0 && apartments[0].id === addAllApartmentsId;
  const areAllApartmentsAdded = optionsData.length === 0;
  const [assignAllApartmentsToGroupAcMutation, { isLoading: isAssignAllAptsLoading }] =
    useAssignAllApartmentsToGroupMutation();

  useEffect(() => {
    if (open) {
      dispatch(actions.getAccessControlApartments(groupId));
    }
  }, [open]);

  function handleSubmit(data: ApartmentFormData) {
    const { floor, name, number } = data;
    if (isAddAllApartmentsSelected) {
      assignAllApartmentsToGroupAcMutation({
        companyId: companyId,
        groupId: groupId,
        siteId: siteId,
      })
        .unwrap()
        .then(() => {
          dispatch(addSnackbarSuccess(messages.accessSnackbarApartmentsAddedSuccessfully));
          onSuccess();
        })
        .catch(() => {
          dispatch(addSnackbarError(messages.accessSnackbarApartmentsErrorGeneral, TYPE_DESCRIPTOR));
        });
    } else {
      dispatch(
        actions.addAccessGroupApartments(
          groupId,
          data.apartment.filter((item) => item.id !== newItemId).map((item) => item.id),
          data.hasForm ? { floor, name, number } : null
        )
      );
    }
    onClose();
  }

  const getIsOpen = open || isLoading || isAssignAllAptsLoading;
  return (
    getIsOpen && (
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <PrimaryModal
            actionButtonLabel={formatMessage(messages.accessGroupAddApartmentModalAdd)}
            isActionButtonDisabled={!isDirty || !isValid}
            isLoading={isLoading || isAssignAllAptsLoading}
            name="access-group-addApartment-modal"
            onClose={onClose}
            onCreate={() => {}}
            open={getIsOpen}
            title={formatMessage(messages.accessGroupAddApartmentModalTitle)}
            noCloseReasons={['backdropClick']}
          >
            <ModalText />
            <ApartmentAutocomplete
              hasAddAllApartmentsOption={true}
              apartmentFormStyle={undefined}
              disabled={false}
              addAllApartmentsId={addAllApartmentsId}
              areAllApartmentsAdded={areAllApartmentsAdded}
              hasFormItemKey={hasFormItemKey}
              loading={isOptionsLoading}
              newItemId={newItemId}
              optionsData={optionsData}
              scrollableTags={true}
            />
            {isAddAllApartmentsSelected && (
              <Alert severity="info">
                <FormattedMessage {...messages.accessGroupAddApartmentModalAlert} />
              </Alert>
            )}
          </PrimaryModal>
        </form>
      </FormProvider>
    )
  );
}

function ModalText() {
  return (
    <Typography component="span" variant="body1">
      <FormattedMessage {...messages.accessGroupAddApartmentModalText} values={{ p: (chunks) => <p>{chunks}</p> }} />
    </Typography>
  );
}

export default AccessGroupAddApartmentModal;
